import { Info } from 'lucide-preact'
import type { SendingDomainRateLimitsDto, SendingDomainRateWindowDto } from '@/types/sendingDomains'
import { formatCount, formatLimit, formatRate } from '@/utils/sendingDomainWarmup'
import { COMPLAINTS_UNDERREPORTED, rateMeterLabel, stopRuleSentence } from '@/utils/sendingDomainStatus'
import { BlockHeading } from './DomainPageParts'

interface RateMeterProps {
  label: string
  percent: number
  decimals: 1 | 2
  holdAbove: number
  stopAbove: number
  /** From the API, which owns the thresholds: whether this rate lets the domain move up, and whether it stops it. */
  allowsMovingUp: boolean
  overStopLimit: boolean
  /** What the rate is out of, e.g. "1,062 sent". */
  outOf: string
  count: number
}

/**
 * One three-day rate against the domain's two lines: where it holds and where it stops. The broadcast RateMeter draws
 * a single limit, so it cannot show both.
 */
function WarmupRateMeter({ label, percent, decimals, holdAbove, stopAbove, allowsMovingUp, overStopLimit, outOf, count }: RateMeterProps) {
  const value = formatRate(percent, decimals)
  const fill = Math.min(percent / stopAbove, 1) * 100
  const holdMark = (holdAbove / stopAbove) * 100
  const tone = overStopLimit
    ? { value: 'text-red-700 dark:text-red-400', bar: 'bg-red-500' }
    : !allowsMovingUp
      ? { value: 'text-amber-700 dark:text-amber-400', bar: 'bg-amber-500' }
      : { value: 'text-slate-900 dark:text-white', bar: 'bg-emerald-500' }

  return (
    <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{label}</span>
        <span className={`text-sm font-semibold tabular-nums ${tone.value}`}>{value}</span>
      </div>
      <div
        role="img"
        aria-label={rateMeterLabel({ rate: value, outOf, holdAbove, stopAbove, overStopLimit })}
        className="relative mt-2 h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800"
      >
        <div className={`h-full rounded-full ${tone.bar}`} style={{ width: `${fill}%` }} />
        <span className="absolute -top-1 h-3.5 w-px bg-slate-500" style={{ left: `${holdMark}%` }} />
      </div>
      <div className="relative mt-1 h-4 text-[11px] text-slate-500 dark:text-slate-400" aria-hidden="true">
        <span className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${holdMark}%` }}>Holds above {formatLimit(holdAbove)}</span>
      </div>
      <div className="mt-1 flex items-center justify-between gap-2 text-[11px]" aria-hidden="true">
        {overStopLimit ? <span className="font-medium text-red-700 dark:text-red-400">Over limit</span> : <span />}
        <span className="text-slate-500 dark:text-slate-400">Stops above {formatLimit(stopAbove)}</span>
      </div>
      <p className="mt-1 text-[11px] text-slate-600 dark:text-slate-400">{formatCount(count)} of {outOf}</p>
    </div>
  )
}

/** The domain's hard bounces and complaints over the last three days, against where it holds and where it stops. */
export function LastThreeDays({ rates, limits, explainStop = false }: {
  rates: SendingDomainRateWindowDto
  limits: SendingDomainRateLimitsDto
  explainStop?: boolean
}) {
  const sent = `${formatCount(rates.Sent)} sent`
  const delivered = `${formatCount(rates.Delivered)} delivered`
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <BlockHeading>Last 3 days</BlockHeading>
        <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400">{sent} · {delivered}</span>
      </div>
      <div className="grid items-start gap-3 sm:grid-cols-2">
        <WarmupRateMeter
          label="Hard bounces"
          percent={rates.HardBouncePercent}
          decimals={1}
          holdAbove={limits.MaxHardBouncePercentToMoveUp}
          stopAbove={limits.StopHardBouncePercent}
          allowsMovingUp={rates.HardBouncesAllowMovingUp}
          overStopLimit={rates.HardBouncesOverStopLimit}
          outOf={sent}
          count={rates.HardBounced}
        />
        <WarmupRateMeter
          label="Spam complaints"
          percent={rates.ComplaintPercent}
          decimals={2}
          holdAbove={limits.MaxComplaintPercentToMoveUp}
          stopAbove={limits.StopComplaintPercent}
          allowsMovingUp={rates.ComplaintsAllowMovingUp}
          overStopLimit={rates.ComplaintsOverStopLimit}
          outOf={delivered}
          count={rates.Complained}
        />
      </div>
      {explainStop && <p className="max-w-prose text-[13px] text-slate-600 dark:text-slate-400">{stopRuleSentence(limits)}</p>}
      <p className="flex max-w-prose items-start gap-2 text-[13px] text-slate-600 dark:text-slate-400">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden="true" />
        <span>{COMPLAINTS_UNDERREPORTED}</span>
      </p>
    </div>
  )
}
